import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  BudgetItem,
  BudgetItemSummary,
  BudgetMacroItemComplete,
} from '@libs/core/api';
import { ModalController } from '@ionic/angular';
import { IonicBaseComponent } from '@front-end/core/ionic-hooks';
import {
  ArticleItem,
  ArticleSelectListComponent,
  MacroCategory,
  SubItem,
} from '../article-select-list/article-select-list.component';

interface DirectArticleItem {
  article: ArticleItem;
  item: SubItem;
  category: MacroCategory;
}

@Component({
  selector: 'ui-budget-item-select-modal',
  templateUrl: './budget-item-select-modal.component.html',
  styleUrls: ['./budget-item-select-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubtaskSelectModalComponent
  extends IonicBaseComponent
  implements OnInit
{
  @ViewChild(ArticleSelectListComponent)
  articleSelectList?: ArticleSelectListComponent;

  readonly TOTAL_BUDGET = 'totalBudget';
  readonly RESIDUAL_BUDGET = 'residualBudget';
  readonly USED_BUDGET = 'usedBudget';

  macroItems: BudgetMacroItemComplete[] = [];
  selectedTask?: BudgetMacroItemComplete;
  selectedSubtask?: BudgetItemSummary;
  selectedArticle?: ArticleItem;

  filterProperty = '';
  filterConfig = [
    {
      key: this.TOTAL_BUDGET,
      label: 'pages.insert.form.subtaskModal.filters.total',
    },
    {
      key: this.RESIDUAL_BUDGET,
      label: 'pages.insert.form.subtaskModal.filters.residual',
    },
    {
      key: this.USED_BUDGET,
      label: 'pages.insert.form.subtaskModal.filters.approved',
    },
  ];

  categories: MacroCategory[] = [];
  directArticles: DirectArticleItem[] = [];
  isDirectArticleSelection = false;

  // Budget item already chosen (projectTasks holds just that one): open
  // directly on its articles.
  @Input() startAtArticles = false;

  @Input() set projectTasks(value: BudgetMacroItemComplete[]) {
    this.macroItems = value;
    this.categories = this.convertToCategories(value);
    this.isDirectArticleSelection = this.shouldUseDirectArticleSelection(
      this.categories
    );
    this.directArticles = this.isDirectArticleSelection
      ? this.flattenDirectArticles(this.categories)
      : [];
    this.changeDetector.markForCheck();
  }

  @Input() set selected(value: BudgetItemSummary | undefined) {
    if (value) {
      this.selectedSubtask = value;
    }
  }

  constructor(
    private modalController: ModalController,
    private changeDetector: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.startAtArticles) {
      this.selectedTask = this.macroItems[0];
      this.selectedSubtask = this.macroItems[0]?.budgetItems?.[0];
    }
  }

  convertToCategories(tasks: BudgetMacroItemComplete[]): MacroCategory[] {
    return tasks.map((task) => ({
      id: task.id,
      name: task.name,
      items: (task.budgetItems || []).map((budgetItem) => ({
        id: budgetItem.id,
        name: budgetItem.name,
        code: budgetItem.code,
        totalBudget: budgetItem.totalBudget,
        residualBudget: budgetItem.residualBudget,
        usedBudget: budgetItem.usedBudget,
        itemCount: budgetItem.projectArticles?.length || 0,
        articles: (budgetItem.projectArticles || []).map((article) => ({
          id: article.id,
          name: article.description,
          code: article.number,
        })),
      })),
    }));
  }

  shouldUseDirectArticleSelection(categories: MacroCategory[]): boolean {
    if (categories.length !== 1) {
      return false;
    }

    const items = categories[0].items ?? [];

    if (items.length !== 1) {
      return false;
    }

    return (items[0].articles?.length ?? 0) === 1;
  }

  flattenDirectArticles(categories: MacroCategory[]): DirectArticleItem[] {
    return categories.flatMap((category) =>
      (category.items ?? []).flatMap((item) =>
        (item.articles ?? []).map((article) => ({
          article,
          item,
          category,
        }))
      )
    );
  }

  closeOrGoBack(): void {
    if (this.startAtArticles) {
      this.close();
      return;
    }

    if (this.articleSelectList) {
      if (this.articleSelectList.currentView !== 'categories') {
        this.articleSelectList.goBack();
        return;
      }
    }

    this.close();
  }

  onArticleSelected(article: ArticleItem): void {
    this.selectedArticle = article;
    this.changeDetector.markForCheck();
  }

  onItemSelected(item: SubItem): void {
    this.selectedSubtask = item;
    this.changeDetector.markForCheck();
  }

  onCategorySelected(category: MacroCategory): void {
    this.selectedTask = category;
    this.changeDetector.markForCheck();
  }

  onDirectArticleSelected(directArticle: DirectArticleItem): void {
    this.selectedTask = directArticle.category;
    this.selectedSubtask = directArticle.item;
    this.selectedArticle = directArticle.article;
    this.changeDetector.markForCheck();
  }

  isDirectArticleSelected(directArticle: DirectArticleItem): boolean {
    return (
      this.selectedArticle?.id === directArticle.article.id &&
      this.selectedSubtask?.id === directArticle.item.id
    );
  }

  confirm(): void {
    if (!this.selectedArticle || !this.selectedSubtask) {
      this.modalController.dismiss(null, 'cancel');
      return;
    }

    const budgetItem: BudgetItem = {
      id: this.selectedSubtask.id,
      name: this.selectedSubtask.name,
      code: this.selectedSubtask.code,
      totalBudget: this.selectedSubtask.totalBudget,
      residualBudget: this.selectedSubtask.residualBudget,
      usedBudget: this.selectedSubtask.usedBudget,
      macroItem: this.selectedTask,
      projectArticles: [
        {
          id: this.selectedArticle.id,
          description: this.selectedArticle.name,
          number: this.selectedArticle.code,
        },
      ],
    };

    this.modalController.dismiss(budgetItem, 'confirm');
  }

  close(): void {
    this.modalController.dismiss();
  }
}
